import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SafeSEO from '@/components/SafeSEO';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Tag, Loader2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { realtimeDb } from '@/lib/firebase';
import { ref, get } from 'firebase/database';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

const getFallbackNewsImage = (category = '') => {
  const cat = category.toLowerCase();
  if (cat.includes('energ')) return '/images/sector-energy.png';
  if (cat.includes('inmobiliari')) return '/images/sector-realestate.png';
  if (cat.includes('inversi')) return '/images/service-investment.png';
  if (cat.includes('digital') || cat.includes('tecnolog') || cat.includes('eficiencia')) return '/images/service-digital.png';
  if (cat.includes('infraestructura') || cat.includes('log')) return '/images/sector-infra.png';
  return '/images/service-investment.png';
};

const resolveNewsImage = (article) => {
  const raw = article?.imageUrl || article?.image;
  if (!raw || raw.includes('horizons-cdn.hostinger.com')) {
    return getFallbackNewsImage(article?.category);
  }
  return raw;
};

const NewsArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {

    const fetchArticle = async () => {
      try {
        const newsRef = ref(realtimeDb, 'news');
        const snapshot = await get(newsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          // Buscamos el artículo que coincida con el slug
          const foundArticle = Object.values(data).find(item => item.slug === slug);
          
          if (foundArticle) {
            setArticle(foundArticle);
          } else {
            toast({
              title: 'Artículo no encontrado',
              description: 'La noticia que buscas no existe o ha sido movida.',
              variant: 'destructive'
            });
            navigate('/noticias');
          }
        } else {
          toast({
            title: 'No hay noticias',
            description: 'No se encontraron artículos publicados.',
            variant: 'destructive'
          });
          navigate('/noticias');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        toast({
          title: 'Error',
          description: 'No se pudo cargar el artículo.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchArticle();
    } else {
      setLoading(false);
      navigate('/noticias');
    }
  }, [slug, navigate, toast]);

  const handleShare = async () => {
    if (!article) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: 'Enlace copiado',
          description: 'El enlace al artículo ha sido copiado al portapapeles.'
        });
      }
    } catch (error) {
      console.error('Error sharing article:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-[#0A5F7F]" />
      </div>
    );
  }

  if (!article) return null;

  return (
    <article className="bg-white min-h-screen pt-20">
      <SafeSEO
        title={article.title}
        description={article.excerpt || article.title}
        imageUrl={resolveNewsImage(article)}
      />

      <div className="relative h-[60vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src={resolveNewsImage(article)}
            alt={article.title}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getFallbackNewsImage(article.category);
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 pb-16 pt-32 px-6">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link
                to="/noticias"
                className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('news.back')}
              </Link>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <span className="bg-[#0A5F7F] text-white text-sm px-4 py-1.5 rounded-full font-semibold flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {article.category || 'General'}
                </span>

                <span className="flex items-center text-white/90 text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  {article.displayDate || article.date}
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center justify-between border-t border-white/20 pt-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-3 backdrop-blur-sm">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">
                      {t('news.writtenBy')}
                    </p>
                    <p className="text-white/80 text-sm">
                      {article.author || 'Consulting Logistic'}
                    </p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 hover:text-white"
                  onClick={handleShare}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('news.share')}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg prose-slate max-w-none
              prose-headings:text-[#0A5F7F]
              prose-a:text-[#0D7FA3] hover:prose-a:text-[#0A5F7F]
              prose-blockquote:border-l-[#0A5F7F] prose-blockquote:bg-sky-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
              prose-img:rounded-xl prose-img:shadow-lg"
            dangerouslySetInnerHTML={{ __html: article.content || '<p>Sin contenido disponible.</p>' }}
          />

          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="bg-sky-50 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-[#0A5F7F] mb-4">
                {t('news.interestedTitle')}
              </h3>

              <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                {t('news.interestedDesc')}
              </p>

              <Link to="/contacto">
                <Button className="bg-[#0A5F7F] hover:bg-[#0D7FA3] text-white px-8 py-6 text-lg h-auto">
                  {t('news.contactExpert')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default NewsArticle;
